import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import Hero from "../components/Hero";
import Services, { SectionHead } from "../components/Services";
import Pricing from "../components/Pricing";
import DemoCards from "../components/DemoCards";
import Contact from "../components/Contact";
import Marquee from "../components/Marquee";
import About from "../components/About";
// Testimonials hidden until real client quotes are ready — see Testimonials.jsx
// import Testimonials from "../components/Testimonials";
import { niches, whyChoose } from "../data/services";
import { useLanguage } from "../context/LanguageContext";

export default function Home() {
  const { t, dir, lang } = useLanguage();

  return (
    <>
      <Hero />

      <Marquee items={t.marquee} accent />

      <Services />

      <About />

      {/* Who We Help */}
      <section
        id="who-we-help"
        dir={dir}
        lang={lang}
        className="relative py-24 md:py-32 px-5 sm:px-8"
        data-testid="who-we-help-section"
      >
        <div className="mx-auto max-w-7xl">
          <SectionHead
            eyebrow={t.whoWeHelp.eyebrow}
            title={t.whoWeHelp.title}
            sub={t.whoWeHelp.sub}
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {niches.map((n, i) => {
              const Icon = Lucide[n.icon] || Lucide.Sparkles;
              const tn = t.whoWeHelp.niches[i];
              return (
                <motion.div
                  key={n.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-agency-gray to-black p-8 hover-lift"
                  data-testid={`niche-card-${n.name.toLowerCase()}`}
                >
                  <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-agency-purple/30 blur-3xl group-hover:bg-agency-gold/20 transition-colors duration-700" />
                  <Icon size={28} className="text-agency-gold" />
                  <h3 className="mt-6 font-display text-2xl font-bold">{tn.name}</h3>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-sm">
                    {tn.blurb}
                  </p>
                  <Link
                    to={`/demo/${n.name.toLowerCase() === "cafés" ? "cafe" : n.name.toLowerCase().replace(/s$/, "")}`}
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-agency-gold"
                  >
                    {t.whoWeHelp.liveDemo}
                    <span className="h-px w-8 bg-agency-gold transition-all group-hover:w-12" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why SS */}
      <section
        id="why-us"
        dir={dir}
        lang={lang}
        className="relative py-24 md:py-32 px-5 sm:px-8"
        data-testid="why-us-section"
      >
        <div className="mx-auto max-w-7xl grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow={t.whyUs.eyebrow}
              title={t.whyUs.title}
              sub={t.whyUs.sub}
            />
          </div>
          <div className="lg:col-span-7 grid gap-px bg-white/5 border border-white/5">
            {whyChoose.map((w, i) => {
              const Icon = Lucide[w.icon] || Lucide.Sparkles;
              const tw = t.whyUs.items[i];
              return (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="group bg-agency-gray p-6 md:p-8 hover:bg-black transition-colors"
                  data-testid={`why-card-${i}`}
                >
                  <div className="flex items-start gap-5">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-black border border-agency-gold/30 text-agency-gold">
                      <Icon size={18} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg md:text-xl font-bold">
                        {tw.title}
                      </h3>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed">
                        {tw.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <DemoCards />

      {/* <Testimonials /> */}

      <Pricing />
      <Contact />
    </>
  );
}
